import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useCssHandles } from '../hooks/useCssHandles'
import { useCart } from '../../modules/cart/contexts/CartContext'
import { useAuth } from '../../modules/auth/contexts/AuthContext'
import api from '../../services/api'
import { Battery } from '../../types/battery'
import Input from './Input'

const CSS_HANDLES = [
  'header',
  'headerContainer',
  'logo',
  'logoImage',
  'navigation',
  'navList',
  'navItem',
  'navLink',
  'searchBar',
  'searchInput',
  'searchButton',
  'userActions',
  'userLinks',
  'userLink',
  'userMenu',
  'userMenuButton',
  'userMenuDropdown',
  'userMenuItems',
  'userMenuItem',
  'userInfo',
  'userName',
  'userEmail',
  'logoutButton',
  'minicart',
  'minicartButton',
  'minicartIcon',
  'minicartBadge',
  'minicartQuantity',
  'mobileMenu',
  'mobileMenuButton',
  'mobileMenuIcon',
  'mobileMenuOverlay',
  'mobileMenuContent',
  'mobileMenuLinks'
] as const

const Header: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)
  const { getTotalItems, openCart } = useCart()
  const { user, isAuthenticated, logout } = useAuth()
  const navigate = useNavigate()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  
  // Estados para busca
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<Battery[]>([])
  const [isSearchLoading, setIsSearchLoading] = useState(false)
  const [showSearchResults, setShowSearchResults] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const debounceTimerRef = useRef<number | null>(null)

  const totalItems = getTotalItems()
  const isAdminUser = user?.role === 'ADMIN'

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen)
  }

  const handleLogout = () => {
    logout()
    setIsUserMenuOpen(false)
  }

  // Função para buscar produtos
  const searchProducts = useCallback(async (term: string) => {
    if (term.length < 3) {
      setSearchResults([])
      setShowSearchResults(false)
      return
    }

    setIsSearchLoading(true)
    try {
      const response = await api.get<{ success: boolean; data: { products: Battery[]; pagination?: any } }>('/products/search', {
        params: { q: term, limit: 8, page: 1 }
      })
      
      if (response.data.success) {
        setSearchResults(response.data.data.products)
        setShowSearchResults(true)
      }
    } catch (error) {
      console.error('Erro ao buscar produtos:', error)
      setSearchResults([])
      setShowSearchResults(false)
    } finally {
      setIsSearchLoading(false)
    }
  }, [])

  // Debounce para busca
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    if (searchTerm.length >= 3) {
      debounceTimerRef.current = window.setTimeout(() => {
        void searchProducts(searchTerm)
      }, 300) // 300ms de debounce
    } else {
      setSearchResults([])
      setShowSearchResults(false)
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [searchTerm, searchProducts])

  // Fechar menu do usuário quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false)
      }
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchResults(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Fechar resultados ao pressionar ESC
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowSearchResults(false)
        searchInputRef.current?.blur()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [])

  // Handlers para busca
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchTerm.trim().length >= 3) {
      setShowSearchResults(false)
      navigate(`/catalogo?search=${encodeURIComponent(searchTerm.trim())}`)
      setSearchTerm('')
      searchInputRef.current?.blur()
    }
  }

  const handleProductClick = (slug: string) => {
    setShowSearchResults(false)
    setSearchTerm('')
    navigate(`/produto/${slug}`)
    searchInputRef.current?.blur()
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price)
  }

  return (
    <>
      <header className={`${handles.header} bg-white/95 backdrop-blur-md shadow-lg sticky top-0 z-50 border-b border-gray-100`}>
        <div className={`${handles.headerContainer} max-w-7xl mx-auto px-4`}>
          <div className="flex items-center justify-between h-20 gap-3">
            {/* Logo */}
            <div className={`${handles.logo} flex-shrink-0 mr-5`}>
              <Link to="/" className="flex items-center group">
                <img src={'/assets/logo-cor-sem-slogan.svg'} alt="Logo da Norte Sul Baterias" className={`${handles.logoImage} h-[60px]`} />
              </Link>
            </div>

            {/* Navegação */}
            <nav className={`${handles.navigation} hidden lg:flex`}>
              <ul className={`${handles.navList} flex space-x-8`}>
                <li className={`${handles.navItem}`}>
                  <Link 
                    to="/catalogo" 
                    className={`${handles.navLink} text-gray-700 hover:text-blue-600 font-semibold transition-all duration-300 relative group`}
                  >
                    Produtos
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 transition-all duration-300 group-hover:w-full"></span>
                  </Link>
                </li>
                <li className={`${handles.navItem}`}>
                  <Link 
                    to="/marcas" 
                    className={`${handles.navLink} text-gray-700 hover:text-blue-600 font-semibold transition-all duration-300 relative group`}
                  >
                    Marcas
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 transition-all duration-300 group-hover:w-full"></span>
                  </Link>
                </li>
                <li className={`${handles.navItem}`}>
                  <Link 
                    to="/sobre" 
                    className={`${handles.navLink} text-gray-700 hover:text-blue-600 font-semibold transition-all duration-300 relative group`}
                  >
                    Sobre
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 transition-all duration-300 group-hover:w-full"></span>
                  </Link>
                </li>
              </ul>
            </nav>

            {/* Barra de Pesquisa */}
            <div className={`${handles.searchBar} flex-1 max-w-xl mx-8 hidden md:block`} ref={searchRef}>
              <form onSubmit={handleSearchSubmit} className="relative">
                <Input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={() => {
                    if (searchResults.length > 0) {
                      setShowSearchResults(true)
                    }
                  }}
                  placeholder="Buscar produto..."
                  className={`${handles.searchInput} w-full px-6 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:border-transparent focus:bg-white transition-all duration-300`}
                />
                <button
                  type="submit"
                  className={`${handles.searchButton} absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-blue-600 transition-colors duration-300`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>

                {/* Dropdown de Resultados */}
                {showSearchResults && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-[500px] overflow-y-auto">
                    {isSearchLoading ? (
                      <div className="p-6 text-center">
                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <p className="mt-2 text-sm text-gray-500">Buscando...</p>
                      </div>
                    ) : searchResults.length > 0 ? (
                      <>
                        <div className="p-2">
                          {searchResults.map((product) => (
                            <button
                              key={product.id}
                              onClick={() => handleProductClick(product.slug)}
                              className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors text-left group"
                            >
                              {/* Imagem do Produto */}
                              <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-lg overflow-hidden">
                                {product.images && product.images.length > 0 ? (
                                  <img
                                    src={product.images[0]}
                                    alt={product.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement
                                      target.src = '/assets/placeholder-battery.png'
                                    }}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                    </svg>
                                  </div>
                                )}
                              </div>

                              {/* Informações do Produto */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    {product.brand && (
                                      <p className="text-xs font-semibold text-blue-600 mb-0.5 truncate">
                                        {product.brand}
                                      </p>
                                    )}
                                    <h4 className="text-sm font-medium text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
                                      {product.name}
                                    </h4>
                                    {product.model && (
                                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                                        {product.model}
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex-shrink-0 text-right">
                                    <p className="text-sm font-bold text-gray-900">
                                      {formatPrice(product.price)}
                                    </p>
                                    {product.originalPrice && product.originalPrice > product.price && (
                                      <p className="text-xs text-gray-400 line-through">
                                        {formatPrice(product.originalPrice)}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                        <div className="border-t border-gray-200 p-2">
                          <button
                            onClick={handleSearchSubmit}
                            className="w-full text-center text-sm font-medium text-blue-600 hover:text-blue-700 py-2 rounded-lg hover:bg-blue-50 transition-colors"
                          >
                            Ver todos os resultados para "{searchTerm}"
                          </button>
                        </div>
                      </>
                    ) : searchTerm.length >= 3 ? (
                      <div className="p-6 text-center">
                        <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm text-gray-500">Nenhum produto encontrado</p>
                        <p className="text-xs text-gray-400 mt-1">Tente buscar com outros termos</p>
                      </div>
                    ) : null}
              </div>
                )}
              </form>
            </div>

            {/* Ações do Usuário */}
            <div className={`${handles.userActions} flex items-center space-x-4`}>
              {isAuthenticated ? (
                /* Menu do Usuário Logado */
                <div className={`${handles.userMenu} relative`} ref={userMenuRef}>
                  <button
                    onClick={toggleUserMenu}
                    className={`${handles.userMenuButton} flex items-center space-x-2 text-gray-700 hover:text-blue-600 font-medium transition-all duration-300 hover:scale-105`}
                  >
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {user?.name?.charAt(0).toUpperCase()}
                    </div>
                    <span className="hidden md:block">{user?.name?.split(' ')[0]}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isUserMenuOpen && (
                    <div className={`${handles.userMenuDropdown} absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50`}>
                      <div className={`${handles.userInfo} px-4 py-3 border-b border-gray-100`}>
                        <div className={`${handles.userName} font-semibold text-gray-900`}>
                          {user?.name}
                        </div>
                        <div className={`${handles.userEmail} text-sm text-gray-600`}>
                          {user?.email}
                        </div>
                      </div>
                      
                      <div className={`${handles.userMenuItems} py-2`}>
                        {isAdminUser ? (
                          <>
                            <Link 
                              to="/admin" 
                              className={`${handles.userMenuItem} flex items-center px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors`}
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0h6" />
                              </svg>
                              Área Administrativa
                            </Link>
                            <button
                              onClick={handleLogout}
                              className={`${handles.logoutButton} w-full flex items-center px-4 py-2 text-red-600 hover:bg-red-50 transition-colors`}
                            >
                              <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                              </svg>
                              Sair
                            </button>
                          </>
                        ) : (
                          <>
                            <Link 
                              to="/conta" 
                              className={`${handles.userMenuItem} flex items-center px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors`}
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              Minha Conta
                            </Link>
                            <Link 
                              to="/pedidos" 
                              className={`${handles.userMenuItem} flex items-center px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors`}
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                              Meus Pedidos
                            </Link>
                            <button
                              onClick={handleLogout}
                              className={`${handles.logoutButton} w-full flex items-center px-4 py-2 text-red-600 hover:bg-red-50 transition-colors`}
                            >
                              <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                              </svg>
                              Sair
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Links para Usuário Não Logado */
                <div className={`${handles.userLinks} hidden md:flex items-center space-x-4`}>
                  <Link 
                    to="/login" 
                    className={`${handles.userLink} flex items-center space-x-2 text-gray-700 hover:text-blue-600 font-medium transition-all duration-300 hover:scale-105`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                    </svg>
                    <span>Entrar</span>
                  </Link>
                </div>
              )}

              {/* Minicart */}
              <div className={`${handles.minicart} relative`}>
                <button
                  onClick={openCart}
                  className={`${handles.minicartButton} relative p-3 text-gray-700 hover:text-blue-600 transition-all duration-300 hover:scale-110 bg-gray-50 hover:bg-blue-50 rounded-xl`}
                >
                  <svg className={`${handles.minicartIcon} w-6 h-6`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                  {totalItems > 0 && (
                    <span className={`${handles.minicartBadge} absolute -top-1 -right-1 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-bold shadow-lg animate-pulse`}>
                      <span className={`${handles.minicartQuantity}`}>{totalItems}</span>
                    </span>
                  )}
                </button>
              </div>

              {/* Menu Mobile */}
              <button 
                onClick={toggleMobileMenu}
                className={`${handles.mobileMenuButton} md:hidden p-3 text-gray-700 hover:text-blue-600 transition-all duration-300 hover:scale-110 bg-gray-50 hover:bg-blue-50 rounded-xl`}
              >
                <svg className={`${handles.mobileMenuIcon} w-6 h-6 transition-transform duration-300 ${isMobileMenuOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <>
          {/* Overlay escuro com animação */}
          <div 
            className={`${handles.mobileMenuOverlay} fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden transition-opacity duration-300 ease-in-out`} 
            onClick={toggleMobileMenu}
            style={{ animation: 'fadeIn 0.3s ease-in-out' }}
          />
          
          {/* Menu lateral deslizante */}
          <div 
            className={`${handles.mobileMenuContent} fixed top-0 right-0 bottom-0 w-80 max-w-[85vw] bg-white shadow-2xl z-50 md:hidden overflow-y-auto`} 
            onClick={(e) => e.stopPropagation()}
            style={{ animation: 'slideInRight 0.3s ease-out' }}
          >
            <div className={`${handles.mobileMenuLinks} flex flex-col h-full`}>
              {/* Header do Menu */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
                <button 
                  onClick={toggleMobileMenu}
                  className="p-2 text-gray-500 hover:text-gray-900 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Informações do Usuário */}
              {isAuthenticated ? (
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                      {user?.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        Olá, {user?.name?.split(' ')[0]}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 border-b border-gray-200">
                  <Link 
                    to="/login"
                    onClick={toggleMobileMenu}
                    className="block w-full py-3 px-4 bg-blue-600 text-white text-center font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Entrar ou Cadastrar-se
                  </Link>
                </div>
              )}

              {/* Navegação Principal */}
              <nav className="flex-1 overflow-y-auto">
                <div className="py-2">
                  <Link 
                    to="/baterias" 
                    className="flex items-center px-4 py-3 text-gray-900 hover:bg-gray-50 border-b border-gray-100"
                    onClick={toggleMobileMenu}
                  >
                    <span className="font-medium">Baterias</span>
                  </Link>
                  
                  <Link 
                    to="/marcas" 
                    className="flex items-center px-4 py-3 text-gray-900 hover:bg-gray-50 border-b border-gray-100"
                    onClick={toggleMobileMenu}
                  >
                    <span className="font-medium">Marcas</span>
                  </Link>
                  
                  <Link 
                    to="/sobre" 
                    className="flex items-center px-4 py-3 text-gray-900 hover:bg-gray-50 border-b border-gray-100"
                    onClick={toggleMobileMenu}
                  >
                    <span className="font-medium">Sobre Nós</span>
                  </Link>
                </div>

                {/* Seção da Conta (se autenticado) */}
              {isAuthenticated && (
                  <div className="py-2 mt-4 border-t-4 border-gray-200">
                    {isAdminUser ? (
                      <>
                        <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">
                          Administração
                        </div>
                        <Link 
                          to="/admin" 
                          className="flex items-center px-4 py-3 text-gray-900 hover:bg-gray-50 border-b border-gray-100"
                          onClick={toggleMobileMenu}
                        >
                          <span className="font-medium">Área Administrativa</span>
                        </Link>
                        <button
                          onClick={() => {
                            handleLogout()
                            toggleMobileMenu()
                          }}
                          className="flex items-center w-full px-4 py-3 text-red-600 hover:bg-red-50 border-b border-gray-100 font-medium"
                        >
                          Sair
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">
                          Minha Conta
                        </div>
                        
                        <Link 
                          to="/conta" 
                          className="flex items-center px-4 py-3 text-gray-900 hover:bg-gray-50 border-b border-gray-100"
                          onClick={toggleMobileMenu}
                        >
                          <span className="font-medium">Dados Cadastrais</span>
                        </Link>
                        
                        <Link 
                          to="/pedidos" 
                          className="flex items-center px-4 py-3 text-gray-900 hover:bg-gray-50 border-b border-gray-100"
                          onClick={toggleMobileMenu}
                        >
                          <span className="font-medium">Meus Pedidos</span>
                        </Link>
                        
                        <button
                          onClick={() => {
                            handleLogout()
                            toggleMobileMenu()
                          }}
                          className="flex items-center w-full px-4 py-3 text-red-600 hover:bg-red-50 border-b border-gray-100 font-medium"
                        >
                          Sair
                        </button>
                      </>
                    )}
                  </div>
                )}
              </nav>
            </div>
          </div>

          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            
            @keyframes slideInRight {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
          `}</style>
        </>
      )}
    </>
  )
}

export default Header

